// src/components/ApplicationForm.tsx

import React, { useState, useEffect } from 'react';
import TextEditor from "../../components/texteditor/TextEditor";
import { instance } from '../../api/axiosInstance';
import Cookies from "js-cookie"; // 수동 설정을 위해 필요
import { useNavigate } from 'react-router-dom';

const ApplicationForm = () => {
  const [applicantType, setApplicantType] = useState('');
  const [teamName, setTeamName] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [userId, setUserId] = useState('');
  const [contentType, setContentType] = useState('');
  const [contentLink, setContentLink] = useState('');
  const [communityActivity, setCommunityActivity] = useState('');
  const [futurePlans, setFuturePlans] = useState('');
  const [submissionStatus, setSubmissionStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();

  // 로컬 스토리지에서 username 가져오기
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUserId(parsedUser.username);
    }
  }, []);

  // 제출 핸들러
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setIsSubmitting(true);
    setSubmissionStatus('');

    // 폼 데이터 구성
    const formData = {
      applicantType,
      teamName: applicantType === '팀' ? teamName : undefined,
      name,
      email,
      contentType,
      contentLink,
      communityActivity,
      futurePlans,
    };

    // 'access_token' 쿠키에서 토큰 가져오기
    const accessToken = Cookies.get('access_token'); // 쿠키 이름 확인

    if (!accessToken) {
      console.error('인증 토큰이 없습니다.');
      setSubmissionStatus('인증 토큰이 없습니다. 다시 로그인 해주세요.');
      setIsSubmitting(false);
      navigate('/login');
      return;
    }

    try {
      // Axios 요청에 Authorization 헤더 수동으로 추가
      const response = await instance.post('/auth/application-forms/submit', formData, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      // 성공 시 상태 업데이트 및 폼 초기화
      setSubmissionStatus('신청이 성공적으로 제출되었습니다. 심사 결과는 7일 이내로 메일로 안내드립니다.');
      setApplicantType('');
      setTeamName('');
      setName('');
      setEmail('');
      setContentType('');
      setContentLink('');
      setCommunityActivity('');
      setFuturePlans('');
    } catch (error: any) {
      console.error('신청서 제출 실패:', error);

      if (error.response) {
        // 서버에서 응답을 받은 경우
        setSubmissionStatus(`신청서 제출 실패: ${error.response.data.message || '알 수 없는 오류입니다.'}`);
      } else if (error.request) {
        // 요청이 이루어졌으나 응답을 받지 못한 경우
        setSubmissionStatus('신청서 제출 실패: 서버에 응답이 없습니다.');
      } else {
        // 기타 오류
        setSubmissionStatus(`신청서 제출 실패: ${error.message}`);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="max-w-5xl w-full mx-auto mt-22 h-auto pb-24 px-4">

      <div className="mt-32">
        <h2 className="text-3xl font-bold mb-6 text-center">파트너 신청 양식</h2>

        {/* 파트너 신청에 대한 간단한 설명 추가 */}
        <p className="text-center text-gray-600">
          파트너 신청을 통해 크릿스토어에서 더 많은 상품을 판매 및 활동을 진행해보세요.
        </p>
        <p className="text-center text-gray-600 mb-[45px]">
          모든 신청서는 신중하게 검토되며, 필요한 경우 추가 정보가 요청될 수 있습니다.
        </p>
        <form onSubmit={handleSubmit} className="space-y-8">

          {/* CREAIT 사용자 ID */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700">CREAIT 사용자 ID</label>
            <p className="text-xs text-gray-500 mb-2">귀하의 CREAIT 사용자 ID는 파트너 계정으로 이용되며 자동으로 입력됩니다.</p>
            <input
              type="text"
              value={userId}
              disabled
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring focus:ring-blue-500 focus:border-blue-500 p-2 bg-gray-100"
            />
          </div>

          {/* 개인 or 팀 선택 */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700">신청 유형</label>
            <p className="text-xs text-gray-500 mb-2">개인 또는 팀으로 신청할 수 있습니다.</p>
            <select
              value={applicantType}
              onChange={(e) => setApplicantType(e.target.value)}
              required
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring focus:ring-blue-500 focus:border-blue-500 p-2"
            >
              <option value="">신청 유형을 선택하세요</option>
              <option value="개인">개인</option>
              <option value="팀">팀</option>
            </select>
          </div>

          {/* 팀 이름 (팀일 경우에만 표시) */}
          {applicantType === '팀' && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700">팀 이름</label>
              <p className="text-xs text-gray-500 mb-2">팀 이름을 입력해주세요.</p>
              <input
                type="text"
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                required={applicantType === '팀'}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring focus:ring-blue-500 focus:border-blue-500 p-2"
                placeholder="팀 이름을 입력하세요"
              />
            </div>
          )}

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700">닉네임</label>
            {applicantType === '팀' ? (
              <p className="text-xs text-gray-500 mb-2">팀 대표자의 닉네임을 입력해주세요.</p>
            ) : (
              <p className="text-xs text-gray-500 mb-2">파트너 신청자의 닉네임을 입력해주세요.</p>
            )}
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring focus:ring-blue-500 focus:border-blue-500 p-2"
              placeholder="이름을 입력하세요"
            />
          </div>

          {/* 이메일 주소 */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700">이메일 주소</label>
            <p className="text-xs text-gray-500 mb-2">연락 가능한 이메일 주소를 입력해주세요. 해당 이메일로 결과가 전송됩니다.</p>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring focus:ring-blue-500 focus:border-blue-500 p-2"
              placeholder="이메일을 입력하세요"
            />
          </div>

          {/* 주요 콘텐츠 유형 */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700">주요 콘텐츠 유형</label>
            <p className="text-xs text-gray-500 mb-2">주로 제작하는 콘텐츠 유형을 선택해주세요.</p>
            <select
              value={contentType}
              onChange={(e) => setContentType(e.target.value)}
              required
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring focus:ring-blue-500 focus:border-blue-500 p-2"
            >
              <option value="">콘텐츠 유형을 선택하세요</option>
              <option value="모델링">모델링</option>
              <option value="건축">건축</option>
              <option value="엔티티">엔티티</option>
              <option value="픽셀">픽셀</option>
              <option value="스크립트">스크립트</option>
              <option value="스킨">스킨</option>
              <option value="스킬">스킬</option>
              <option value="플러그인">플러그인</option>
              <option value="기타">기타</option>
            </select>
          </div>

          {/* 콘텐츠 링크 */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700">콘텐츠 링크</label>
            <p className="text-xs text-gray-500 mb-2">주요 콘텐츠를 확인할 수 있는 링크를 입력해주세요.</p>
            <input
              type="url"
              value={contentLink}
              onChange={(e) => setContentLink(e.target.value)}
              required
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring focus:ring-blue-500 focus:border-blue-500 p-2"
              placeholder="콘텐츠 링크를 입력하세요"
            />
          </div>

          {/* 커뮤니티 활동 */}
          <div className="mb-[20px]">
            <label className="block text-sm font-medium text-gray-700">파트너 소개</label>
            {applicantType === '팀' ? (
              <p className="text-xs text-gray-500 mb-2">팀에 대해서 소개해주세요. 활동하신 프로젝트, 경력이 있으시면 어필해주세요.</p>
            ) : (
              <p className="text-xs text-gray-500 mb-2">본인에 대해서 소개하고 활동하신 프로젝트, 경력이 있으시면 어필해주세요.</p>
            )}
            <TextEditor
              value={communityActivity}
              onChange={setCommunityActivity}
              height="300px"
            />
          </div>

          {/* 향후 계획 */}
          <div className="mb-6">
            <label className="mt-[70px] block text-sm font-medium text-gray-700">향후 계획</label>
            <p className="text-xs text-gray-500 mb-2">향후 계획에 대해 설명해주세요.</p>
            <TextEditor
              value={futurePlans}
              onChange={setFuturePlans}
              height="300px"
            />
          </div>

          {/* 제출 버튼 */}
          <button
            type="submit"
            className={`w-full bg-blue-600 text-white py-2 rounded-md hover:bg-blue-700 transition-colors ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            disabled={isSubmitting}
          >
            {isSubmitting ? '제출 중...' : '신청 제출'}
          </button>
        </form>

        {/* 제출 상태 메시지 */}
        {submissionStatus && (
          <p className={`mt-6 text-center ${submissionStatus.includes('성공') ? 'text-green-600' : 'text-red-600'}`}>
            {submissionStatus}
          </p>
        )}
      </div>
    </section>
  );
};

export default ApplicationForm;
